import { DateAxis, Legend, LineSeries, ValueAxis, XYChart, XYCursor } from '@amcharts/amcharts4/charts';
import { BindingEngine } from 'aurelia-binding';
import { widget } from '../../widgets/widget-decorator';
import { color, create, percent, unuseTheme, useTheme } from '@amcharts/amcharts4/core';
import { customElement, useView, PLATFORM } from 'aurelia-framework';
import { GridStack, GridStackItem } from 'aurelia-gridstack';
import { TaskQueue } from 'aurelia-framework';
import { Filter } from '../../types/filter';
import { InstrumentValueGroup } from '../../clients/reporting-clients';
import { ChartWidgetBase } from '../chart-widget-base';
import { CustomerService } from '../../services/customer-service';
import { IApplicationFilter } from '../../interfaces/i-application-filter';
import { getChartReadyPromise } from '../../services/chart-ready';
import { PortfolioReportCache } from '../../services/portfolio-report-cache';
import { WidgetPropertyType } from '../widget-property-type';
import { instrumentValueFields } from '../instrument-value-fields';
import { InstrumentTypesCache } from '../../services/instrument-types-cache';
import { property } from '../property-decorator';

export enum DimensionOverTimeChartWidgetStartFrom {
  FromDate = 1,
  FirstTransaction = 2
}

export enum DimensionOverTimeChartWidgetGroupBy {
  Type = 2,
  Category = 3
}

@customElement('dimension-over-time-widget')
@widget('Dimension over time', true, true)
@useView(PLATFORM.moduleName('./dimension-over-time-widget.html'))
export class DimensionOverTimeChartWidget extends ChartWidgetBase {
  constructor(element: Element, customerService: CustomerService, bindingEngine: BindingEngine, private instrumentTypesCache: InstrumentTypesCache,
    private gridStackItem: GridStackItem, private taskQueue: TaskQueue, private gridStack: GridStack, private portfolioReportCache: PortfolioReportCache) {
    super(element, bindingEngine, customerService);
  }

  chartObject: XYChart;
  instrumentGroups: InstrumentValueGroup[];
  legendData: {
    group: string;
    groupId: number;
    profitLoss: number;
    estimatedValue: number;
    profitLossPercent?: number;
    estimatedValuePercent?: number;
    colour: string;
  }[];
  legendDiv: HTMLDivElement;
  isIgnite = this.gridStack.root.classList.contains('ignite');

  @property('Data', undefined, WidgetPropertyType.List, [{ value: DimensionOverTimeChartWidgetStartFrom.FromDate, label: '"From" date' }, { value: DimensionOverTimeChartWidgetStartFrom.FirstTransaction, label: 'First transaction' }])
  startFrom: DimensionOverTimeChartWidgetStartFrom = DimensionOverTimeChartWidgetStartFrom.FromDate;

  @property('Data', undefined, WidgetPropertyType.List, instrumentValueFields.map(f => ({ value: f.name, label: f.label })))
  aggregatedField: Filter<InstrumentValueGroup, number> = 'estimatedValue';

  @property('Data', undefined, WidgetPropertyType.List, [{ value: DimensionOverTimeChartWidgetGroupBy.Type, label: 'Type' }, { value: DimensionOverTimeChartWidgetGroupBy.Category, label: 'Category' },])
  groupBy: DimensionOverTimeChartWidgetGroupBy = DimensionOverTimeChartWidgetGroupBy.Type;

  @property('Data', 'Category type', WidgetPropertyType.List)
  categoryTypeId: number | undefined;
  async categoryTypeIdListValues() {
    return this.customerService.instrumentCategories.filter(x => x.enabled).map(x => ({ value: x.catTypeId, label: x.catTypeName }));
  }

  @property('Data')
  percentage: boolean;

  @property('Data')
  legend: boolean;

  @property('Data')
  chartLegend: boolean;

  @property('Data', 'Instrument type', WidgetPropertyType.List)
  instrumentTypeId: number;
  async instrumentTypeIdListValues() {
    return [
      { value: undefined, label: 'All' },
      ... (await this.instrumentTypesCache.instrumentTypes).map(x => ({ value: x.id, label: x.name }))
    ];
  }

  @property()
  headerFont?: string;

  @property()
  headerFontSize?: number;

  @property()
  headerColor?: string;

  @property('Text')
  font?: string;

  @property('Text')
  fontSize?: number;

  @property('Text')
  color?: string;

  @property('Style')
  chartRowSpan?: number;

  @property('Style')
  chartTheme: string;

  @property('Style')
  labelColour?: string;

  override async loadData(filter: IApplicationFilter) {
    const categoryTypeId = this.groupBy === DimensionOverTimeChartWidgetGroupBy.Category ? this.categoryTypeId : undefined;
    const dateFrom = this.startFrom === DimensionOverTimeChartWidgetStartFrom.FromDate ? filter.dates.fromStr : '2000-01-01';
    return this.portfolioReportCache.instrumentValueGroupsOverTime(filter.customer.companyId, categoryTypeId, dateFrom, filter.dates.toStr, false);
  }

  override async setData(data: InstrumentValueGroup[]) {
    if (this.groupBy === DimensionOverTimeChartWidgetGroupBy.Type && this.instrumentTypeId) {
      this.instrumentGroups = data.filter(x => x.groupId === this.instrumentTypeId);
    }
    else {
      this.instrumentGroups = data;
    }
    await this.createChart();
  }

  async createChart() {
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, XYChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.height = percent(100);
    this.chartObject.hiddenState.properties.opacity = 0;
    this.chartObject.fontSize = 9;
    const labelColour = color(this.getChartLabelColour(this.labelColour));
    if (this.chartLegend) {
      this.chartObject.legend = new Legend();
      this.chartObject.legend.labels.template.fill = labelColour;
    }

    const xAxis = this.chartObject.xAxes.push(new DateAxis());
    xAxis.dataFields.date = 'date';
    xAxis.renderer.labels.template.wrap = true;
    xAxis.renderer.labels.template.rotation = -90;
    xAxis.renderer.minGridDistance = 30;
    xAxis.renderer.grid.template.location = 0.5;
    xAxis.renderer.labels.template.horizontalCenter = 'right';
    xAxis.renderer.labels.template.verticalCenter = 'middle';
    xAxis.renderer.labels.template.fill = labelColour;
    xAxis.renderer.ticks.template.stroke = labelColour;

    const yAxis = this.chartObject.yAxes.push(new ValueAxis());
    yAxis.numberFormatter.numberFormat = '#a';
    yAxis.tooltip.align = 'right';
    yAxis.tooltip.contentAlign = 'right';
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    yAxis.updateTooltip = () => { };
    yAxis.tooltip.pointerOrientation = 'left';
    yAxis.renderer.labels.template.fill = labelColour;
    yAxis.renderer.ticks.template.stroke = labelColour;

    const groups = this.instrumentGroups.groupBy(x => x.group);
    let byDates = this.instrumentGroups.groupBy(x => x.date);
    // add missing dates so that stacked chart works
    groups.forEach((values, group) => {
      const missingDates = Array.from(byDates.keys()).filter(x => !values.some(y => y.date === x));
      values.push(...missingDates.map(x => new InstrumentValueGroup({ date: x, group, groupId: values[0].groupId, estimatedValue: 0 })));
    });

    if (this.percentage) {
      // group again to take into account added missing dates
      byDates = this.instrumentGroups.groupBy(x => x.date);
      yAxis.max = 100;
      byDates
        .forEach((values) => {
          values.sort((a, b) => a.group.localeCompare(b.group));
          const total = values.reduce((p, c) => p += c[this.aggregatedField], 0);
          for (let i = 0; i < values.length; ++i) {
            values[i][this.aggregatedField] = values[i][this.aggregatedField] / total * 100;
          }
        });
    }

    this.legendData = [];
    this.chartObject.interpolationDuration = 0;
    groups.forEach((values, group) => {
      const series = this.chartObject.series.push(new LineSeries());
      series.interpolationDuration = 0;
      series.name = group;
      series.stacked = true;
      series.dataFields.valueY = this.aggregatedField;
      series.dataFields.dateX = 'date';
      series.data = values.sort((a, b) => a.date.localeCompare(b.date));
      series.fillOpacity = 0.5;
      series.tooltipText = `[bold]{name}[/] {valueY.formatNumber("#,###.00")}${this.percentage ? '%' : ''}`;
      series.strokeWidth = 2;
      series.strokeOpacity = 1;
      // series.fill = this.chartObject.colors.getIndex(i);
      series.fillOpacity = 0.5;
      const lastValue = values[values.length - 1];
      this.legendData.push({
        group,
        groupId: lastValue.groupId,
        profitLoss: lastValue.unrealizedProfitLoss ?? 0,
        estimatedValue: lastValue.estimatedValue,
        colour: series.fill.toString(),
      });
    });

    const { profitLossSum, estimatedValueSum } = this.legendData.reduce((acc, curr) => {
      return {
        profitLossSum: acc.profitLossSum + curr.profitLoss,
        estimatedValueSum: acc.estimatedValueSum + curr.estimatedValue
      };
    }, { profitLossSum: 0, estimatedValueSum: 0 });
    this.legendData.forEach((x, i) => {
      x.profitLossPercent = x.profitLoss / profitLossSum * 100;
      x.estimatedValuePercent = x.estimatedValue / estimatedValueSum * 100;
    });
    this.legendData.sort((a, b) => b.profitLoss - a.profitLoss);

    this.chartObject.cursor = new XYCursor();
    unuseTheme(theme);
    await readyPromise;
    if (this.gridStack.grid) {
      this.chart.style.height = this.chartRowSpan ? `${this.chartRowSpan * (this.gridStack.grid.opts.cellHeight as number)}px` : '';

      if (!this.gridStackItem.root.classList.contains('grid-stack-item--edited') && this.legendDiv) {
        this.taskQueue.queueTask(() => {
          if (this.legendDiv.clientHeight < this.legendDiv.scrollHeight) {
            // add missing height
            // set slightly larger so that there is a gap between this widget and the next one when the height is decreased
            this.gridStackItem.h += Math.ceil((this.legendDiv.scrollHeight - this.legendDiv.clientHeight) / (this.gridStack.grid.opts.cellHeight as number)) + 2;
            this.taskQueue.queueTask(() => this.gridStackItem.h -= 2);
          }
        });
      }
    }
  }

  configChanged(key: keyof DimensionOverTimeChartWidget) {
    switch (key) {
      case 'labelColour':
        const c = color(this.labelColour);
        this.chartObject.xAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = c);
        this.chartObject.yAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = c);
        this.refresh$.next();
        break;
      default:
        this.refresh$.next();
        break;
    }
  }

  bind() { }

  showDetails() {
    this.element.dispatchEvent(new CustomEvent('open-view', {
      bubbles: true, detail: {
        view: 'instruments-information',
        params: { instrumentTypeId: this.instrumentTypeId }
      }
    }));
  }
}
